import React, {useContext, useEffect, useRef, useState} from "react";
import Button from "react-bootstrap/Button";
import {Alert, Spinner} from "react-bootstrap";
import AuthContext from "../../../contexts/AuthContext";
import ExecutionsAPI from "../../../services/ExecutionsAPI";
import {useStoreExecutions} from "../../../store/useStoreExecutions";

interface TakePictureInterface {
    order: string;
}

export const TakePicture = ({order}: TakePictureInterface) => {
    const {user} = useContext(AuthContext);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [errorStartCamera, setErrorStartCamera] = useState<string | null>(null);
    const [displayCamera, setDisplayCamera] = useState<boolean>(true);
    const [image, setImage] = useState<string|null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const getExecutions = useStoreExecutions((state) => state.getExecutions);

    const constraints: MediaStreamConstraints = {
        audio: false,
        video: {width: 1280, height: 720},
    };

    const startCamera = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = mediaStream;

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream as MediaStream;
                // Démarrage de la vidéo après que les métadonnées soient initialisées
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current?.play().catch(() => {});
                };
            }
        } catch (error) {
            const e = error as Error;
            setErrorStartCamera(`${e.name || 'Error'}: ${e.message}`);
        }
    };

    useEffect(() => {
        startCamera();

        // Pour arrêter la caméra quand la modale se ferme et quand le composant est démonté
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }

            if (videoRef.current) {
                try {
                    videoRef.current.srcObject = null;
                } catch {
                    // On ne fait rien
                }
            }
        };
    }, []);

    function takePhoto() {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        const canvas = document.createElement('canvas');
        const width = video.videoWidth || 1280;
        const height = video.videoHeight || 720;
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');

        if (!context) {
            return;
        }

        context.drawImage(video, 0, 0, width, height);
        setImage(canvas.toDataURL('image/jpeg', 0.92));
        setDisplayCamera(false);
    }

    function handleUpload() {
        setIsLoading(true);
        upload()
            .then(() => {
                setIsSuccess(true);
                getExecutions(user, true);
            })
            .catch(() => setHasError(true))
            .finally(() => setIsLoading(false));
    }

    async function upload() {
        await ExecutionsAPI.uploadPicture(order, {
            content: image,
        });
    }

    function handleRetry() {
        startCamera();
        setDisplayCamera(true);
    }

    function renderContent() {
        if (displayCamera) {
            return (
                <div className="w-100 text-center">
                    {errorStartCamera ? (
                        <Alert variant={'danger'} className={'mt-2'}>
                            errorStartCamera
                        </Alert>
                    ) : (
                        <>
                            <video ref={videoRef} autoPlay playsInline className={'w-100 rounded'} />
                            <div className={'mt-2 w-100 text-center'}>
                                <Button variant={'primary'} onClick={takePhoto}>
                                    Prendre la photo
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            );
        }

        // Loading lors du clic sur "Sauvegarder"
        if (isLoading) {
            return <Spinner />;
        }

        // Message de succès
        if (isSuccess && !hasError) {
            return <Alert variant={'success'}>Photo enregistrée avec succès.</Alert>;
        }

        // Message d'erreur
        if (!isSuccess && hasError) {
            return (
                <Alert variant={'danger'} className={'mt-2'}>
                    Une erreur est survenue lors de l'enregistrement de la photo.
                </Alert>
            );
        }

        return (
            <>
                <div className={'w-100 text-center'}>
                    <img src={image} alt={'Photo prise'} className={'img-fluid mx-auto mb-2'} />
                    <Button variant={'secondary'} onClick={handleRetry} className={'mr-3'}>Reprendre la photo</Button>
                    <Button variant={'secondary'} onClick={handleUpload}>Sauvegarder</Button>
                </div>
            </>
        );
    }

    return (
        <>
            {renderContent()}
        </>
    );
};
